type RGBA = { r: number; g: number; b: number; a: number };

/**
 * Parses a CSS color string (#rgb, #rrggbb, #rgba, #rrggbbaa, rgb(), rgba())
 * into individual RGBA components. Returns `null` when the format is
 * unsupported.
 */
export const parseColorToRgba = (color: string): RGBA | null => {
  if (!color) return null;

  // HEX formats ------------------------------------------------------------
  if (color[0] === "#") {
    let hex = color.slice(1);

    // Expand shorthand (#abc / #rgba) → full form
    // For example, #RGB becomes #RRGGBB and #RGBA becomes #RRGGBBAA.
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }

    // Convert the full HEX string to RGBA components.
    if (hex.length === 6 || hex.length === 8) {
      const bigint = parseInt(hex, 16);
      const hasAlpha = hex.length === 8;

      // Extract R, G, B, and optionally A components using bitwise shifts.
      return {
        r: (bigint >> (hasAlpha ? 24 : 16)) & 255,
        g: (bigint >> (hasAlpha ? 16 : 8)) & 255,
        b: (bigint >> (hasAlpha ? 8 : 0)) & 255,
        a: hasAlpha ? (bigint & 255) / 255 : 1,
      };
    }

    return null;
  }

  // RGB / RGBA formats -----------------------------------------------------
  // Attempt to parse rgb(R, G, B) or rgba(R, G, B, A) formats.
  // Regex captures the numerical values for R, G, B, and optionally A.
  const match = color
    .replace(/\s+/g, "") // Remove whitespace for easier parsing
    .match(/^rgba?\((\d+),(\d+),(\d+)(?:,([\d.]+))?\)$/i);

  if (match) {
    // Extract captured values and convert them to numbers.
    // Alpha defaults to 1 (fully opaque) if not specified.
    return {
      r: Number(match[1]),
      g: Number(match[2]),
      b: Number(match[3]),
      a: match[4] !== undefined ? Number(match[4]) : 1,
    };
  }

  return null; // Unsupported / named color
};

/**
 * Blends a foreground color with alpha transparency over a background color,
 * calculating the resulting combined color as if they were layered on top of each other.
 * Returns the final color as an RGBA object.
 */
export const compositeColors = (bg: RGBA, fg: RGBA): RGBA => {
  // Calculate the resulting alpha value using the standard alpha compositing formula:
  // α_out = α_fg + α_bg * (1 - α_fg)
  const a = fg.a + bg.a * (1 - fg.a);

  // If the resulting color is fully transparent, return black with 0 alpha.
  if (a === 0) {
    return { r: 0, g: 0, b: 0, a: 0 };
  }

  // Calculate the resulting RGB values using the standard formula:
  // C_out = (C_fg * α_fg + C_bg * α_bg * (1 - α_fg)) / α_out
  // where C represents R, G, or B components.
  return {
    r: Math.round((fg.r * fg.a + bg.r * bg.a * (1 - fg.a)) / a),
    g: Math.round((fg.g * fg.a + bg.g * bg.a * (1 - fg.a)) / a),
    b: Math.round((fg.b * fg.a + bg.b * bg.a * (1 - fg.a)) / a),
    a,
  };
};

/** Converts an opaque RGBA object to a CSS `rgb(r, g, b)` string representation. */
export const rgbaToRgbString = (rgba: RGBA): string =>
  `rgb(${rgba.r}, ${rgba.g}, ${rgba.b})`;
